/*
 *  -----------------------------------------------------------------------
 *    <copyright file="study-summary/observations-section-v2.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@apollo/client/react';
import { ExtraMenuItemPosition } from '../../core/grid-header/grid-menu/grid-menu.interface';
import { ObsRowAction } from '../observations-grid-v2.interface';
import { ObservationsSectionV2Props } from './observations-section-v2.interface';
import NavigationService from '@components-clean/page-nav/navigation.service';
import {
    CentralMonitoringActionFields,
    CentralMonitoringObservationAndActionFields,
} from 'queries/generated/fragments.generated';
import { GET_SITES_DELETE } from 'queries/sites';
import { filterByType, getPanelName } from 'utils/cm-observations.service';
import ObservationsGridV2 from 'views/components/central-monitoring/observations-grid-v2';
import { AllRoutes } from '@utils/router/all-routes';

const ObservationsSectionV2 = ({
    onOpenObservationsModal,
    onOpenActionModal,
    study,
    canEdit,
    studyData,
    datasetDate,
    data,
    ownerOptions,
    datasetDates,
    handleDeleteObs,
    handleRestoreObs,
}: ObservationsSectionV2Props) => {
    const navigate = useNavigate();
    const { data: siteDeletedData, loading: isSiteLoading } = useQuery(GET_SITES_DELETE, {
        variables: {
            studyId: study?.id,
        },
    });
    const [observations, setObservations] = useState([]);

    useEffect(() => {
        if (data?.centralMonitoringObservations && studyData && datasetDate && !isSiteLoading) {
            const array = data.centralMonitoringObservations
                .filter((observation: CentralMonitoringObservationAndActionFields) =>
                    filterByType(observation, 'study', datasetDate, datasetDates),
                )
                .map((observation: CentralMonitoringObservationAndActionFields) => {
                    const _activeActions = observation.actions?.filter(
                        (action: CentralMonitoringActionFields) => !action.deleted,
                    );
                    const _recentActionDate = _activeActions
                        .map((action: any) => action.requestDate)
                        .sort()
                        .reverse()[0];
                    const numberOfOpenActions = _activeActions.filter((action: any) => !action.completedDate);
                    const owner: any = observation.ownerId
                        ? ownerOptions.find((el: any) => el.id === observation.ownerId)
                        : null;

                    const foundSite = studyData?.sites?.find(
                        (item: any) => item.Fields['Site.id'] === observation.siteId,
                    );
                    let _site = foundSite ? { ...foundSite } : null;

                    if (siteDeletedData?.sitesDelete?.length) {
                        if (_site == null) {
                            const deletedSite = siteDeletedData?.sitesDelete?.find(
                                (site: any) => site.id === observation.siteId,
                            );
                            _site = deletedSite ? { ...deletedSite } : null;
                            if (_site) {
                                _site.Fields = {
                                    ...(typeof _site.Fields === 'object' ? _site.Fields : {}),
                                    'Site.name': _site.name,
                                    'Site.id': _site.id,
                                    'Site.status': _site.status,
                                };
                                _site.Deleted = true;
                            }
                        } else {
                            const isSiteDeleted = siteDeletedData?.sitesDelete?.find(
                                (site: any) => site.id === _site?.Fields['Site.id'],
                            );
                            if (isSiteDeleted) _site.Deleted = true;
                        }
                    }

                    return {
                        ...observation,
                        confirmedCause: observation.confirmedCause ?? '',
                        comments: observation.comments ?? '',
                        potentialCause: observation.potentialCause ?? '',
                        _recentActionDate: _recentActionDate ? new Date(_recentActionDate) : null,
                        _openAction: numberOfOpenActions.length,
                        _numberOfActions: _activeActions.length,
                        _owner: owner,
                        _ownerLabel: owner?.text ?? '',
                        _datasetDate: observation.dataSet?.dataCutDate ?? null,
                        _panelName: getPanelName(observation.panel, observation.type),
                        _isClosedDateReadOnly:
                            observation.actions.findIndex((el: any) => !el.completedDate && !el.deleted) >= 0,
                        _site: _site?.Fields['Site.name'] ?? '',
                        // fields needed for View Site Dashboard btn
                        _siteStatus: _site?.Fields['Site.status'] ?? '',
                        _siteId: _site?.Fields['Site.id'] ?? '',
                        _siteDeleted: _site?.Deleted ?? false,
                        // end
                        ...(observation.deleted && observation.audit
                            ? {
                                  _deleteReason: observation.audit.reason,
                                  _deleteDate: observation.audit.created,
                                  _deletedBy: `${observation.audit.userFirstName} ${observation.audit.userLastName}`,
                              }
                            : {}),
                        actions: observation.actions.map((action: CentralMonitoringActionFields) => {
                            const actionOwner: any = action.ownerId
                                ? ownerOptions.find((el: any) => el.id === action.ownerId)
                                : null;
                            return {
                                ...action,
                                observationId: observation.id,
                                _obsDeleted: observation.deleted,
                                _owner: actionOwner,
                                _site: _site?.Fields['Site.name'] ?? '',
                                // fields needed for View Site Dashboard btn
                                _siteStatus: _site?.Fields['Site.status'] ?? '',
                                _siteId: _site?.Fields['Site.id'] ?? '',
                                _siteDeleted: _site?.Deleted ?? false,
                                // end
                                ownerLabel: actionOwner?.text ?? '',
                                _isCompletedDateReadOnly: Boolean(observation.closedDate),
                                ...(action.deleted && action.audit
                                    ? {
                                          _deleteReason: action.audit.reason,
                                          _deleteDate: action.audit.created,
                                          _deletedBy: `${action.audit.userFirstName} ${action.audit.userLastName}`,
                                      }
                                    : {}),
                            };
                        }),
                    };
                });
            setObservations(array);
        }
    }, [data, studyData, ownerOptions, datasetDate, datasetDates, siteDeletedData, isSiteLoading]);

    const rowActions: ObsRowAction = (item) => {
        if (item.deleted) {
            return [
                {
                    label: 'cm:restoreObservation',
                    icon: ['fal', 'undo'],
                    onClick: () => handleRestoreObs(item),
                    enabled: canEdit && !datasetDate?.isCMR,
                },
            ];
        }

        return [
            {
                label: 'ra:editObservation',
                icon: ['fal', 'edit'],
                onClick: () => onOpenObservationsModal(item),
                enabled: canEdit && !datasetDate?.isCMR,
            },
            {
                label: 'ra:assignAction',
                icon: ['fal', 'link'],
                onClick: () => onOpenActionModal(null, item),
                enabled: canEdit && !item.closedDate && !datasetDate?.isCMR,
            },
            {
                label: 'cm:deleteObservation',
                icon: ['fal', 'trash'],
                onClick: () => handleDeleteObs(item),
                enabled: canEdit && !datasetDate?.isCMR,
            },
            {
                label: 'cm:viewSiteDashboard',
                icon: ['far', 'eye'],
                onClick: () => {
                    NavigationService.setViewLegacySiteDashboard();
                    navigate(AllRoutes.path('cm-site-dashboard') as string, {
                        state: {
                            skipSelectDate: true,
                            showDashboard: true,
                            siteId: item._siteId as string | null | undefined,
                        },
                    });
                },
                enabled:
                    item._siteDeleted === false && item._siteStatus?.length
                        ? (study.sitesStatusesToExclude?.indexOf(item._siteStatus) < 0 ?? true)
                        : false,
            },
        ];
    };

    return (
        <ObservationsGridV2
            className="opra-grid"
            observations={observations}
            study={{ ...study, members: [...ownerOptions] }}
            isCMR={datasetDate?.isCMR}
            rowActions={rowActions}
            hideCheckbox
            extraMenuItems={[
                {
                    position: ExtraMenuItemPosition.expandMenu,
                    customClassName: 'grid-header__item--always-visible',
                    expandMenuItems: [
                        {
                            label: 'ra:addObservation',
                            icon: ['fal', 'plus'],
                            onClick: () => onOpenObservationsModal(),
                            enabled: canEdit && !datasetDate?.isCMR,
                        },
                    ],
                },
            ]}
            canEdit={canEdit}
            withinSplitter
        />
    );
};

export default ObservationsSectionV2;
